import React, {useCallback, useEffect, useState} from 'react';

import {PAYMENT_METHODS} from '@optimaxdev/analytics/desktop/constants/paymentMethods';
import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {connect} from 'react-redux';

import {ApplicationStoreType} from 'desktop/store';
import {AmazonType, DataForSendPayerType} from 'desktop/types/amazonV2Type';
import {OrderReviewTotalsSegmentsType} from 'desktop/types/orderReviewTotalsType';
import {useLoadScript} from 'hooks/useLoadScript/useLoadScript';
import {AMAZON_SCRIPT_SRC} from 'libs/amazon/amazonV2';
import {CompleteOrderType} from 'types/completeOrderType';
import {PaymentType} from 'types/PaymentMethodsType';

import {PAYMENTS} from '../../constants/paymentStep';
import {
    eventAmazonReady,
    methodAmazonSetError,
    fetchAmazonSessionInfoToSaga,
} from '../../store/reducers/paymentStep/amazonV2/amazonV2';
import {sendPayerInfoToSaga} from '../../store/reducers/paymentStep/payerInfo/payerInfo';
import {getCompleteOrderResult} from '../../store/selectors/completeOrder/completeOrder';
import {
    getGrandTotal,
    getOrderReviewTotalsIsLoading,
} from '../../store/selectors/orderReviewTotals/orderReviewTotals';
import {
    getAmazonV2,
    getPaymentMethodByKey,
    getIsSelectedByKey,
} from '../../store/selectors/paymentStep/paymentStep';
import {getShippingMethodsStatus} from '../../store/selectors/shippingMethods/shippingMethods';
import {AmazonPayIcon} from '../icons/checkoutIcons';
import {PaymentAmazonLogin} from '../paymentAmazonLogin/paymentAmazonLogin';
import {PaymentWrapperConnected} from '../paymentWrapper/paymentWrapper';

import {AmazonAuth} from './components/amazonAuth/amazonAuth';
import {sendAmazonPay} from './libs/sendAmazonPay';

export type PropsType = {
    method: PaymentType;
    isSelected: boolean;
    amazonV2: AmazonType;
    completeOrder: CompleteOrderType;
    grandTotal: OrderReviewTotalsSegmentsType;
    orderReviewTotalsIsLoading: boolean;
    shippingMethodsStatus: string;
    setError: (value: string) => void;
    sendPayerInfo: (data: DataForSendPayerType) => void;
    fetchSessionInfo: (sessionId: string) => void;
    callEventAmazonReady: () => void;
};

/**
 * Preparing data for sending the checkoutOption event
 * @param {number} step - step number
 * @param {string} option - additional fields
 */
const prepareDataForPush = (step: number, option: string) => {
    pushDataLayer({
        ecommerce: {
            checkout_option: {
                actionField: {
                    action: 'checkout_option',
                    option: option === PAYMENTS.METHOD_PAYPAL_V3 ? PAYMENT_METHODS[option] : option,
                    step,
                },
            },
        },
        event: 'checkoutOption',
    });
};
/**
 * Payment Amazon for checkout
 *
 * @constructor
 */
export const PaymentAmazon: React.FC<PropsType> = ({
    amazonV2: amazon,
    callEventAmazonReady,
    completeOrder,
    fetchSessionInfo,
    grandTotal,
    isSelected,
    method,
    orderReviewTotalsIsLoading,
    sendPayerInfo,
    setError,
    shippingMethodsStatus,
}) => {
    const [isSending, setIsSending] = useState<boolean>(false);
    const isScriptLoaded = useLoadScript(AMAZON_SCRIPT_SRC);

    const handleSendPayment = useCallback(async () => {
        const isAmazonReady = amazon.paymentReady && amazon.addressSelect;

        setError(isAmazonReady ? '' : 'Please Fill Information in Amazon');

        if (!isAmazonReady) return;

        // Analytics staff
        prepareDataForPush(2, 'amazon_payments');
        setIsSending(true);

        const amazonPayRedirectUrl = await sendAmazonPay(amazon.accessToken, grandTotal.price);

        setIsSending(false);
        window.location.replace(amazonPayRedirectUrl);
    }, [amazon.accessToken, amazon.addressSelect, amazon.paymentReady, grandTotal.price, setError]);

    const handleSavePayment = useCallback(async () => {
        sendPayerInfo({
            accessToken: amazon.accessToken,
            method: method.code,
            title: method.title,
        });
    }, [amazon.accessToken, method.code, method.title, sendPayerInfo]);

    useEffect(() => {
        if (isScriptLoaded) {
            callEventAmazonReady();

            if (amazon.auth) {
                fetchSessionInfo(amazon.accessToken);
            }
        }
    }, [isScriptLoaded]);

    return (
        <PaymentWrapperConnected
            code={method.code}
            isSelected={isSelected}
            title={<AmazonPayIcon />}
        >
            {amazon.auth ? (
                <AmazonAuth
                    amazon={amazon}
                    completeOrder={completeOrder}
                    isLoadingOrderReviewTotals={orderReviewTotalsIsLoading}
                    isLoadingSendOrder={isSending}
                    onSave={handleSavePayment}
                    onSend={handleSendPayment}
                    shippingMethodsStatus={shippingMethodsStatus}
                />
            ) : (
                <PaymentAmazonLogin error={amazon.error} />
            )}
        </PaymentWrapperConnected>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    amazonV2: getAmazonV2(store),
    completeOrder: getCompleteOrderResult(store),
    grandTotal: getGrandTotal(store),
    isSelected: getIsSelectedByKey(store, PAYMENTS.METHOD_AMAZON_V2),
    method: getPaymentMethodByKey(store, PAYMENTS.METHOD_AMAZON_V2),
    orderReviewTotalsIsLoading: getOrderReviewTotalsIsLoading(store),
    shippingMethodsStatus: getShippingMethodsStatus(store),
});

const mapDispatchToProps = {
    callEventAmazonReady: eventAmazonReady,
    fetchSessionInfo: fetchAmazonSessionInfoToSaga,
    sendPayerInfo: sendPayerInfoToSaga,
    setError: methodAmazonSetError,
};

export const PaymentAmazonConnected = connect(mapStateToProps, mapDispatchToProps)(PaymentAmazon);
